import { Dialog, DialogTitle, Typography, IconButton, DialogContent, DialogActions, Button, Box, Radio } from '@mui/material';
import { X } from '../../../assets';
import { useTranslation } from 'react-i18next';
import { Loader } from '../../../components/ui/Loader';
import { StockCheckItem } from './../../../app/services/api/generated';
import { applyRoundAndFormat } from './../../../utils';
import StockCheckCategoryTable from './StockCheckCategoryTable';
import { TempStockCheckItem } from '..';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import useCurrencyCodeConfiguration from '../../../hooks/configurations/useCurrencyCodeConfiguration';

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  reorderEnabled: boolean;
  isApproving: boolean;
  maxToleranceWarningValue?: number;
  itemsAboveMaxTolerance: StockCheckItem[];
  setItemsAboveMaxTolerance: Dispatch<SetStateAction<StockCheckItem[]>>;
  onItemStatusChange: (itemId: number | undefined, checked: boolean) => void;
  updateItems: (items: StockCheckItem[]) => void;
}

const StockCheckApproveDialog = ({ open, onClose, onSubmit, reorderEnabled, isApproving, maxToleranceWarningValue, itemsAboveMaxTolerance, setItemsAboveMaxTolerance, onItemStatusChange, updateItems }: Props) => {
  const { t } = useTranslation('stockCountReview');
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const [isAllItemsApproved, setIsAllItemsApproved] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    getCurrencyCodeConfiguration();
  }, []);

  useEffect(() => {
    if (isAllItemsApproved !== undefined) {
      const updatedItems = itemsAboveMaxTolerance.map(item => {
        item.isApproved = isAllItemsApproved;
        return item;
      });

      setItemsAboveMaxTolerance(updatedItems);

      //need to update parent component state as well to keep in sync
      updateItems(updatedItems);
    }
  }, [isAllItemsApproved]);

  const isAnyItemsInvalid = () => {
    return itemsAboveMaxTolerance.some(item => {
      return item.isApproved !== true && item.isApproved !== false;
    });
  };

  const handleItemStatusChange = (itemId: number | undefined, checked: boolean) => {
    const updatedItems = itemsAboveMaxTolerance.map(item => {
      if (item.id === itemId) {
        return { ...item, isApproved: checked };
      }
      return item;
    });

    setItemsAboveMaxTolerance(updatedItems);
    onItemStatusChange(itemId, checked);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{ p: 0 }}
      fullWidth
      maxWidth={itemsAboveMaxTolerance.length > 0 ? 'lg' : 'sm'}
    >
      <DialogTitle sx={{ m: 4, p: 4 }}>
        {t('approvalDialogTitle')}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.custom.gray[500],
        }}
      >
        <X />
      </IconButton>
      <DialogContent
        sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        <Typography variant='textMD'
          sx={{ my: 4, mx: 6 }}>
          {
            itemsAboveMaxTolerance.length > 0 ? t('approvalDialogWarning', { value: applyRoundAndFormat(maxToleranceWarningValue ?? 0, currencyCode) }) : t('approvalDialogMessage')
          }
        </Typography>
        {
          itemsAboveMaxTolerance.length > 0 &&
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'end', pr: '10px' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', pr: '5px' }}>
                <Typography variant='textSM'>
                  {t('acceptAll')}
                </Typography>
                <Radio
                  checked={isAllItemsApproved === true}
                  value="true"
                  name="is_all_items_approved"
                  onChange={() => { setIsAllItemsApproved(true); }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', pr: '10px' }}>
                <Typography variant='textSM'>
                  {t('rejectAll')}
                </Typography>
                <Radio
                  checked={isAllItemsApproved === false}
                  value="false"
                  name="is_all_items_approved"
                  onChange={() => { setIsAllItemsApproved(false); }}
                />
              </Box>
            </Box>
            <Box sx={{ p: '10px' }}>
              <StockCheckCategoryTable category={{ name: '', items: itemsAboveMaxTolerance as TempStockCheckItem[] }}
                categoryIndex={0}
                reorderEnabled={reorderEnabled}
                sortPartialStockCountItems={() => {}}
                togglePartialStockCountItem={() => {}}
                onItemStatusChange={handleItemStatusChange}
                isDisabled={false}
                isOpenedInDialog
              ></StockCheckCategoryTable>
            </Box>
          </>
        }
      </DialogContent>
      <DialogActions sx={{ mt: 4, mb: 4, mr: 4 }}>
        <Button
          variant="secondary"
          onClick={onClose}
          autoFocus
        >
          {t('close')}
        </Button>
        <Button
          variant="primary"
          onClick={onSubmit}
          disabled={isApproving || isAnyItemsInvalid()}
          startIcon={isApproving && <Loader />}
          autoFocus
        >
          {isApproving ? t('isConfirming') : t('confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StockCheckApproveDialog;